/**
 * CC_SEAT_IDENTITY_MARKS (2026-06-07): shared identity-mark assets so the
 * lobby seat rows (SeatRow) and the in-game menu (EscapeMenu) render the
 * SAME owner glyph from one source — Michael's spec: "same icon,
 * somewhere".
 *
 * EVERY glyph + label below is a PLACEHOLDER pending Michael's art/copy
 * pass. The owner mark is the concierge bell (a hotel touch); the
 * platform/AI tags are stand-in emoji. Swap the glyphs for real art and
 * the copy for Michael's wording without touching the call sites.
 */

// Owner mark — one icon, rendered on the lobby owner's seat row AND in
// the in-game menu. PLACEHOLDER: concierge bell glyph + stand-in copy.
export const OWNER_MARK_GLYPH = '🛎';
export const OWNER_MARK_LABEL = 'Lobby owner';
export const OWNER_MARK_INGAME_LABEL = 'You own this game';

// Platform tags — a seat is exactly one of {discord, web, ai}. The tag
// sits where the platform indicator lives on a seat row. PLACEHOLDER
// glyphs + copy; the discord/web split is a heuristic (see
// platformForSeat) until cross-play lands a ground-truth per-seat
// platform field.
export const PLATFORM_DISCORD = 'discord';
export const PLATFORM_WEB = 'web';
export const PLATFORM_AI = 'ai';

export const PLATFORM_TAG_META = {
    [PLATFORM_DISCORD]: { glyph: '💬', label: 'On Discord' },
    [PLATFORM_WEB]: { glyph: '🌐', label: 'On the web' },
    [PLATFORM_AI]: { glyph: '🤖', label: 'AI player' },
};

/**
 * Resolve a human seat's platform tag.
 *
 * HEURISTIC (PLACEHOLDER): the only per-seat platform signal already on
 * the wire is Seat.platform_display_name — the Discord nick captured at
 * claim time (non-empty => the seat was filled from Discord; web claims
 * store ''). For the LOCAL seat we also trust the live Discord embed
 * context. A non-empty platform_display_name (or an embedded local user)
 * reads as Discord; everything else reads as web.
 *
 * This is a positive-Discord heuristic, NOT ground truth: a Discord user
 * whose nick capture fell through to '' reads as web. The honest
 * per-seat platform source is cross-play model work (UserConnection
 * carries no platform today) owned by CC_CROSS_PLATFORM_PLAY; when that
 * lands a real field, swap the signal here — the call sites don't change.
 *
 * @param {object} params
 * @param {string} [params.platformDisplayName] Seat.platform_display_name.
 * @param {boolean} [params.isLocalUser] Whether this is the viewer's seat.
 * @param {boolean} [params.isEmbedded] Live Discord embed flag.
 * @returns {string} PLATFORM_DISCORD or PLATFORM_WEB.
 */
export function platformForSeat({
    platformDisplayName, isLocalUser, isEmbedded,
}) {
    if (platformDisplayName) {
        return PLATFORM_DISCORD;
    }
    if (isLocalUser && isEmbedded) {
        return PLATFORM_DISCORD;
    }
    return PLATFORM_WEB;
}
